import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { getGetJournalQueryKey } from "@workspace/api-client-react";
import {
  ASSISTANT_API,
  type ActivityEvent,
  type ActivityPage,
  type AssistantConnection,
  type AssistantId,
  type AssistantsOverview,
  type DraftsPage,
  type PermissionsUpdate,
  type StepMark,
  type StepMarksPage,
} from "@workspace/assistants";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { MOMENTUM_QUERY_KEY } from "@/hooks/use-momentum";
import { CALENDAR_SCHEDULES_KEY, useCalendarConnection } from "@/hooks/use-calendar";

export const ASSISTANTS_QUERY_KEY = ["/api/assistants"] as const;
export const ASSISTANT_ACTIVITY_QUERY_KEY = ["/api/assistants/activity"] as const;
export const STEP_MARKS_QUERY_KEY = ["/api/assistants/step-marks"] as const;
export const WAITING_THOUGHTS_QUERY_KEY = ["/api/assistants/drafts"] as const;

async function getJson<T>(path: string): Promise<T> {
  const res = await authFetch(apiUrl(path));
  if (!res.ok) throw new Error(`GET ${path} ${res.status}`);
  return (await res.json()) as T;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await authFetch(apiUrl(path), {
    method,
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `${method} ${path} ${res.status}`);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/** Settings → AI assistants: plan, connector link and each assistant's connection. */
export function useAssistantsOverview(enabled = true) {
  return useQuery({
    queryKey: ASSISTANTS_QUERY_KEY,
    queryFn: () => getJson<AssistantsOverview>(ASSISTANT_API.overview),
    enabled,
  });
}

export function connectionFor(overview: AssistantsOverview | undefined, assistant: AssistantId): AssistantConnection | undefined {
  return overview?.connections.find((c) => c.assistant === assistant);
}

/** Whether "Add steps to your calendar" can be on: Google Calendar connected, ended by Google, or never connected. */
export type AssistantCalendar = "connected" | "needs_reconnect" | "not_connected";

/**
 * The calendar permission follows the calendar layer's own Google connection. Until the
 * calendar API is deployed (or while it loads) it falls back to the overview's flag.
 */
export function useAssistantCalendar(overviewConnected: boolean | undefined): AssistantCalendar {
  const { connection, isLoading, available } = useCalendarConnection();
  if (!available || isLoading) return overviewConnected ? "connected" : "not_connected";
  if (!connection) return "not_connected";
  return connection.status === "connected" ? "connected" : "needs_reconnect";
}

export function useUpdateAssistantPermissions(assistant: AssistantId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: PermissionsUpdate) => send<AssistantConnection>("PATCH", ASSISTANT_API.permissions(assistant), update),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ASSISTANTS_QUERY_KEY }),
  });
}

export function useDisconnectAssistant(assistant: AssistantId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => send<void>("DELETE", ASSISTANT_API.connection(assistant)),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ASSISTANTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ASSISTANT_ACTIVITY_QUERY_KEY });
    },
  });
}

/** What assistants did, newest first. Polled by the activity host while one is connected. */
export function useAssistantActivity(opts: { enabled?: boolean; refetchInterval?: number | false } = {}) {
  return useQuery({
    queryKey: ASSISTANT_ACTIVITY_QUERY_KEY,
    queryFn: async () => (await getJson<ActivityPage>(`${ASSISTANT_API.activity}?limit=50`)).events,
    enabled: opts.enabled ?? true,
    refetchInterval: opts.refetchInterval ?? false,
  });
}

/** Assistant marks for Momentum's step cards ("Done with Claude"), by step id. */
export function useStepMarks(enabled = true): Map<string, StepMark> {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: STEP_MARKS_QUERY_KEY,
    queryFn: async () => {
      const { marks } = await getJson<StepMarksPage>(ASSISTANT_API.stepMarks);
      // An assistant planned or moved a step: the calendar chip reads the calendar layer's
      // schedules, so those refetch too. Checked here, once per fetch, not once per card.
      const before = queryClient.getQueryData<StepMark[]>(STEP_MARKS_QUERY_KEY);
      if (before && planKey(before) !== planKey(marks)) {
        void queryClient.invalidateQueries({ queryKey: CALENDAR_SCHEDULES_KEY }, { cancelRefetch: false });
      }
      return marks;
    },
    enabled,
    staleTime: 30_000,
  });
  return useMemo(() => new Map((data ?? []).map((m) => [m.stepId, m])), [data]);
}

/** The parts of the marks that can mean a calendar change (notes and timestamps can't). */
function planKey(marks: StepMark[]): string {
  return marks
    .map((m) => `${m.stepId}:${m.kind}:${m.scheduled?.start ?? ""}`)
    .sort()
    .join("|");
}

/** Thoughts an assistant sent to wait on Home, newest first. */
export function useWaitingThoughts() {
  return useQuery({
    queryKey: WAITING_THOUGHTS_QUERY_KEY,
    queryFn: async () => (await getJson<DraftsPage>(ASSISTANT_API.drafts)).drafts,
    staleTime: 30_000,
  });
}

export function useSnoozeThought() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, until }: { id: string; until: string }) => send<void>("POST", ASSISTANT_API.snoozeDraft(id), { until }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: WAITING_THOUGHTS_QUERY_KEY }),
  });
}

export function useUnfoldThought() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => send<{ sessionId: string }>("POST", ASSISTANT_API.unfoldDraft(id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: WAITING_THOUGHTS_QUERY_KEY }),
  });
}

/** The person's own note on a step: the same write the journal's "write" box makes. */
export function useSaveStepNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ journalId, stepId, notes }: { journalId: string; stepId: string; notes: string }) =>
      send<unknown>("PATCH", `/journals/${journalId}/actions/${stepId}`, { notes }),
    onSuccess: (_data, { journalId }) => {
      queryClient.invalidateQueries({ queryKey: getGetJournalQueryKey(journalId) });
      queryClient.invalidateQueries({ queryKey: STEP_MARKS_QUERY_KEY });
    },
  });
}

/**
 * After an assistant changes something, everything that shows steps refetches. `events`
 * are the new ones: when an assistant scheduled a step, the calendar layer's schedules
 * refetch too, so Momentum's calendar chip appears without a reload.
 */
export function useRefreshAfterAssistantChanges() {
  const queryClient = useQueryClient();
  return (events: ActivityEvent[] = []) => {
    const keys: QueryKey[] = [MOMENTUM_QUERY_KEY, STEP_MARKS_QUERY_KEY, WAITING_THOUGHTS_QUERY_KEY, ASSISTANTS_QUERY_KEY];
    if (events.some((e) => e.kind === "scheduled")) keys.push(CALENDAR_SCHEDULES_KEY);
    for (const queryKey of keys) queryClient.invalidateQueries({ queryKey });
  };
}
